/*
  File: src/pages/SignupPage.jsx
  Purpose: Registration form that creates a new account via `AuthContext.signup`.
  Responsibilities:
  - Collect name, email, password with confirmation and optional email opt-in.
  - On success, redirect to home; on failure, display server error message.
*/
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

function SignupPage() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(true);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name || !email || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    try {
      await signup({ name, email, password, email_opt_in: emailOptIn });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Signup failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="relative flex-1 flex items-stretch px-0 sm:px-6 py-0 sm:py-10">
        <div className="absolute inset-0">
          <img
            src={`${process.env.PUBLIC_URL}/match.png`}
            alt="hero"
            className="w-full h-full object-cover object-center opacity-40"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-transparent to-black/70" />
        </div>
        <div className="relative flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-8 w-full max-w-7xl mx-auto">
          <section className="hidden lg:flex flex-col justify-center px-10">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 text-sm mb-3">
                {['Football', 'Series', 'Movies', 'Live TV'].map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-white/10 border border-white/10 text-white/80">{t}</span>
                ))}
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight">Join Scene Shop. Stream everything.</h1>
              <p className="mt-4 text-white/70">Create your account to watch live matches, binge your favorite series, and explore a growing library of movies.</p>
              <ul className="mt-6 space-y-2 text-white/80 text-sm">
                <li>• Unlimited streaming on all your devices</li>
                <li>• Cancel anytime</li>
                <li>• Personalized recommendations</li>
              </ul>
            </div>
          </section>
          <div className="flex items-center justify-center p-6 sm:p-10">
            <div className="w-full max-w-md">
              <div className="bg-white/5 backdrop-blur rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-8">
                <div className="text-center mb-6">
                  <div className="mx-auto h-12 w-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                    <span className="text-white text-xl">✨</span>
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight text-white">Create your account</h2>
                  <p className="text-sm text-white/70 mt-1">Join us and get started</p>
                </div>

                {error && <div className="mb-4 rounded-lg bg-red-500/10 p-3 text-sm text-red-300 border border-red-500/30">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Full name</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full border border-white/10 bg-black/40 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-white/50"
                      placeholder="Jane Doe"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border border-white/10 bg-black/40 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-white/50"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full border border-white/10 bg-black/40 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-white/50"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Confirm password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full border border-white/10 bg-black/40 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 placeholder-white/50"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="flex items-start gap-3">
                    <input
                      id="emailOptIn"
                      type="checkbox"
                      checked={emailOptIn}
                      onChange={(e) => setEmailOptIn(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-white/20 bg-black/40 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="emailOptIn" className="text-sm text-white/80">
                      Yes, I would like to receive emails about special promotions and new products. You can opt out anytime.
                    </label>
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full rounded-lg py-2.5"
                  >
                    {isLoading ? 'Creating account…' : 'Create account'}
                  </button>
                </form>

                <p className="mt-6 text-center text-sm text-grayText">
                  Already have an account? <Link to="/login" className="text-secondary font-medium hover:text-softPink">Sign in</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default SignupPage;


